import React from "react";
import { motion } from "framer-motion";
import Logo from "./Logo";
import { ArrowUpRight, Linkedin } from "lucide-react";

/**
 * MIT-style institutional footer / colophon.
 * Replaces the old "contact card" layout with a journal-masthead grid:
 *   ADDRESS · CONTACT · PEOPLE · NEWSROOM
 * with a colophon strip at the bottom (set in / built in / © year).
 */

const ADDRESS = {
  org: "Keimyung AI Research Center",
  parent: "Keimyung University",
  street: "1095 Dalgubeol-daero, Dalseo-gu",
  city: "Daegu 42601, Republic of Korea",
  building: "Bldg. AI-101 · 3F",
};

const CONTACT = [
  { label: "General", value: "segeberg@keimyung.ai", href: "mailto:segeberg@keimyung.ai" },
  { label: "Phone", value: "053-580-8837", href: "tel:0535808837" },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/keimyung-ai-4685903b9",
    icon: true,
  },
];

const PEOPLE = [
  { label: "Faculty & PIs", href: "#team" },
  { label: "Visiting Scholars", href: "#team" },
  { label: "Graduate Students", href: "#team" },
  { label: "Open Positions", href: "mailto:careers@k-airc.org" },
];

const NEWSROOM = [
  { label: "Research Events", href: "#events" },
  { label: "Open Lab Days", href: "#events" },
  { label: "Publications", href: "#research" },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const ContactSection = () => {
  const year = new Date().getFullYear();

  return (
    <footer
      id="contact"
      data-testid="contact-section"
      className="bg-[#0a0a0b] text-white relative overflow-hidden"
    >
      {/* Subtle dot grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 sm:pt-28 pb-10">
        {/* Top meta row */}
        <motion.div
          {...fade(0)}
          className="flex flex-wrap items-baseline gap-x-6 gap-y-2 pb-6 border-b border-white/10 mb-14 sm:mb-20"
        >
          <span className="text-[10px] font-mono tracking-[0.32em] uppercase text-[#00BCD4]">
            § Colophon
          </span>
          <span className="text-[10px] font-mono tracking-[0.32em] uppercase text-white/40">
            The Institute
          </span>
          <span className="ml-auto text-[10px] font-mono tracking-[0.32em] uppercase text-white/40">
            Get in touch ↘
          </span>
        </motion.div>

        {/* Editorial statement */}
        <motion.div
          {...fade(0.08)}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 sm:mb-24"
        >
          <div className="lg:col-span-7">
            <h2 className="text-4xl sm:text-5xl lg:text-[64px] font-light tracking-tight leading-[1.02] text-white">
              An open laboratory for{" "}
              <span className="text-white/40">
                Machines That Understand People.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed mb-6">
              We welcome collaboration with researchers, clinicians,
              policy-makers, journalists, and citizens. Write to us — we read
              every message.
            </p>
            <a
              href="mailto:segeberg@keimyung.ai"
              data-testid="footer-primary-cta"
              className="group inline-flex items-center gap-2 text-[15px] font-light tracking-tight text-white border-b border-white/40 hover:border-[#00BCD4] hover:text-[#00BCD4] pb-1 transition-colors"
            >
              segeberg@keimyung.ai
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </motion.div>

        {/* 4-column masthead */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12 pb-16 sm:pb-20 border-b border-white/10">
          <Column title="Address" delay={0.1}>
            <div className="space-y-1 text-sm text-white/75 font-light leading-relaxed">
              <div className="text-white font-medium">{ADDRESS.org}</div>
              <div>{ADDRESS.parent}</div>
              <div className="pt-2">{ADDRESS.street}</div>
              <div>{ADDRESS.city}</div>
              <div className="text-white/50">{ADDRESS.building}</div>
            </div>
          </Column>

          <Column title="Contact" delay={0.18}>
            <ul className="space-y-2">
              {CONTACT.map((c) => (
                <li key={c.label} className="text-sm">
                  <span className="block text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mb-0.5">
                    {c.label}
                  </span>
                  {c.icon ? (
                    <a
                      href={c.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Keimyung AI on ${c.label}`}
                      data-testid={`footer-${c.label.toLowerCase()}`}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-white/80 hover:text-white hover:bg-[#0A66C2] hover:border-[#0A66C2] transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  ) : (
                    <a
                      href={c.href}
                      className="text-white/85 hover:text-[#00BCD4] transition-colors font-light"
                    >
                      {c.value}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </Column>

          <Column title="People" delay={0.26}>
            <ul className="space-y-2.5">
              {PEOPLE.map((p) => (
                <li key={p.label}>
                  <a
                    href={p.href}
                    className="group inline-flex items-center gap-1.5 text-sm text-white/75 hover:text-white font-light transition-colors"
                  >
                    {p.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </Column>

          <Column title="Newsroom" delay={0.34}>
            <ul className="space-y-2.5">
              {NEWSROOM.map((n) => (
                <li key={n.label}>
                  <a
                    href={n.href}
                    className="group inline-flex items-center gap-1.5 text-sm text-white/75 hover:text-white font-light transition-colors"
                  >
                    {n.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </Column>
        </div>

        {/* Brand row */}
        <motion.div
          {...fade(0.1)}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pt-10 sm:pt-12"
        >
          <div className="flex items-center gap-4">
            <Logo size={42} variant="light" />
            <div>
              <div className="text-base font-medium tracking-tight text-white">
                Keimyung AI Research Center
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mt-1">
                The Center for Artificial Intelligence · Est. MMXXVI
              </div>
            </div>
          </div>

          {/* Big wordmark — typographic flourish */}
          <div className="text-right">
            <div className="text-[40px] sm:text-[56px] lg:text-[72px] font-light leading-none tracking-tight text-white/10 select-none">
              k/ai
            </div>
          </div>
        </motion.div>

        {/* Bottom colophon strip */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[10px] font-mono uppercase tracking-[0.28em] text-white/40">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>© {year} Keimyung AI Research Center</span>
            <span className="text-white/20">·</span>
            <span>All Rights Reserved</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <a href="#privacy" className="hover:text-white/80 transition-colors">
              Privacy
            </a>
            <span className="text-white/20">·</span>
            <a href="#terms" className="hover:text-white/80 transition-colors">
              Terms
            </a>
            <span className="text-white/20">·</span>
            <a href="#ethics" className="hover:text-white/80 transition-colors">
              Research Ethics
            </a>
            <span className="text-white/20">·</span>
            <a href="#accessibility" className="hover:text-white/80 transition-colors">
              Accessibility
            </a>
            <span className="text-white/20">·</span>
            <a
              href="/admin"
              data-testid="footer-admin-link"
              className="hover:text-white/80 transition-colors"
            >
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Column = ({ title, children, delay = 0 }) => (
  <motion.div {...fade(delay)} data-testid={`footer-col-${title.toLowerCase()}`}>
    <div className="flex items-center gap-2 mb-5">
      <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00BCD4]">
        §
      </span>
      <h3 className="text-[10px] font-mono tracking-[0.3em] uppercase text-white/90">
        {title}
      </h3>
    </div>
    {children}
  </motion.div>
);

export default ContactSection;
